// @flow
import striptags from 'striptags';
import URI from 'urijs';
import { constants, url, resortAction } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';

const REGEXP_DYNAMIC_CRUISE = /^CM2C_\d{8}_\d{8}$/;
export const RESORT_PRODUCT_TYPE = 'resort';

function getType(item: SuggestItem): string {
  return item.product_id ? RESORT_PRODUCT_TYPE : item.type || '';
}

function isItemJoyview(item: SuggestItem): boolean {
  return item.type === constants.PRODUCT_TYPES.JOYVIEW;
}

function isItemResort(item: SuggestItem): boolean {
  return getType(item) === RESORT_PRODUCT_TYPE;
}

function getLabel(item: SuggestItem): string {
  const label = striptags(item.value || '', '<span>');
  return isItemResort(item) && item.sibling_id ? `${label} - ${item.season || ''}` : label;
}

function isCruise(item: SuggestItem): boolean {
  return item.type === constants.PRODUCT_TYPES.CRUISE;
}

function isDynamicCruise(item: SuggestItem): boolean {
  return isCruise(item) && REGEXP_DYNAMIC_CRUISE.test(item.product_id || '');
}

function isTour(item: SuggestItem): boolean {
  return item.type === constants.PRODUCT_TYPES.TOUR;
}

function getProductId(item: SuggestItem): ?string {
  return isItemResort(item) ? item.product_id : null;
}

function getProductSeason(item: SuggestItem): string {
  return resortAction.getSeasonInfos(item.sibling_id ? item.season_id : constants.ALL_YEAR);
}

function getProductUrl(item: SuggestItem): string {
  const productUrl = new URI('/');

  if (isDynamicCruise(item)) {
    const segments = ['c', ...(item.product_id || '').split('_')];

    productUrl.segment(segments);
  } else if (isTour(item)) {
    productUrl
      .segment('i')
      .segment(url.encodePath(item.title))
      .segment(getProductSeason(item));
  } else {
    productUrl
      .segment('r')
      .segment(url.encodePath(item.title))
      .segment(getProductSeason(item));
  }

  return productUrl.toString();
}

function getLink(item: SuggestItem): ?string {
  return isItemResort(item) ? getProductUrl(item) : undefined;
}

type ProductHeadlines = {
  subLabel1: ?string,
  subLabel2: ?string,
  image: ?string,
  productId: ?string,
  productType: ?string,
  comfortLevel: ?string,
  season: ?string,
};
function getHeadlines(item: SuggestItem): $Shape<$Exact<ProductHeadlines>> {
  return isItemResort(item)
    ? {
        subLabel1: Array.isArray(item.destination_country)
          ? item.destination_country.join(', ')
          : item.destination_country,
        subLabel2: item.destination_area,
        image: item.immersive_image,
        productId: item.product_id,
        productType: item.type,
        comfortLevel: item.comfort_level,
        season: Array.isArray(item.season) ? '' : item.season,
      }
    : {};
}

function getIcon(item: SuggestItem) {
  if (isItemJoyview(item)) {
    return ICONS.TRIDENT_JOYVIEW;
  }
  if (isTour(item)) {
    return ICONS.TOUR;
  }
  if (isCruise(item)) {
    return ICONS.CRUISE;
  }
  if (isItemResort(item)) {
    return ICONS[`TRIDENT${item.comfort_level || '1'}`];
  }
  return item.type === 'activity' ? ICONS.ACTIVITIES : ICONS.ADDRESS;
}

export type ResultItem = {
  id: string,
  type: string,
  label: string,
  icon: ?string,
  link: ?string,
  product_id: ?string,
  ...$Shape<$Exact<ProductHeadlines>>,
};

export function getResultItem(item: SuggestItem): ResultItem {
  return {
    id: getProductId(item) || getLabel(item),
    type: getType(item),
    label: getLabel(item),
    icon: getIcon(item),
    link: getLink(item),
    product_id: getProductId(item),
    ...getHeadlines(item),
  };
}
